import { Radar } from '@nivo/radar'
import classnames from 'classnames'
import * as React from 'react'
import { isEducator } from 'services/user'
import { getValueClassName } from '../../data/abitilies/Breakpoint'
import { docSize } from '../../services/sizes'
import './style.scss'

interface IProps {
  isPlusUser: boolean
  chartData: {
    labels: string[]
    values: number[]
  }
  onClicks?: Array<() => void>
  goToPlus: () => void
  role: string
}

interface IData {
  label: string
  value: number
  onClick: () => void
}

interface IState {
  data: IData[]
  smallDevice: boolean
}

const plusAbilityList = [
  'Attention',
  'Math',
  'Memory',
  'Flexible Thinking',
  'Spatial',
  'Motor',
  'Self-esteem',
  'Social',
  'Language'
]

const exceptionsList = ['Self-esteem', 'Spatial']

const checkLockedAbility = (
  isPlusUser: boolean,
  ability: string,
  plusAbility = plusAbilityList
): boolean => {
  return !isPlusUser && plusAbility.includes(ability)
}

const notFreemium = (
  isPlusUser: boolean,
  ability: string,
  exceptions = exceptionsList
): boolean => {
  return !(isPlusUser || exceptions.includes(ability))
}

export class Chart extends React.PureComponent<IProps, IState> {
  static getDerivedStateFromProps(nextProps: IProps, prevState: IState) {
    const {
      chartData: { labels, values },
      onClicks
    } = nextProps
    return {
      data: labels.map((label, i) => ({
        label,
        value: values[i],
        onClick: onClicks && onClicks[i]
      }))
    }
  }
  gradStart = '#6318b8'
  gradEnd = '#ff02e9'
  maxValue = 100

  state: IState = {
    data: [],
    smallDevice: false
  }

  key = 0

  setup = () => {
    this.addClipPath()
    this.setLevels()
    this.setChart()
    this.setClicks()
  }

  setClicks = () => {
    const { data } = this.state
    const { goToPlus } = this.props

    data.forEach(d => {
      const label = document.getElementById(`label-${d.label}`)
      if (label) {
        label.onclick =
          checkLockedAbility(this.props.isPlusUser, d.label) &&
          notFreemium(this.props.isPlusUser, d.label)
            ? goToPlus
            : d.onClick
      }
    })
  }

  checkResize = () => {
    const bound = 575
    const { smallDevice } = this.state
    if (window.innerWidth <= bound && !smallDevice) {
      this.setState({ smallDevice: true })
    } else if (window.innerWidth > bound && smallDevice) {
      this.setState({ smallDevice: false })
    } else {
      this.setState({})
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.checkResize)
    this.checkResize()
  }

  componentDidUpdate() {
    this.setup()
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.checkResize)
  }

  getMaxValue = () =>
    Math.max.apply(
      Math,
      this.state.data.map(d => d.value)
    )

  getInnerChartG = () => {
    const parentG = document.querySelector('#radar-chart>div>svg>g>g')!
    const chartG = (parentG.childNodes[1] as Element)!

    const innerG = chartG.querySelector('g')

    if (innerG) {
      return innerG
    }
    const newInnerG = document.createElement('g')
    chartG.appendChild(newInnerG)
    return newInnerG
  }

  addClipPath = () => {
    const parentG = document.querySelector('#radar-chart>div>svg>g>g')!
    const chartG = (parentG.childNodes[1] as Element)!

    const clipPath = document.createElement('clipPath')
    clipPath.setAttribute('id', 'clip')

    const path = document.createElement('path')
    path.setAttribute('id', 'clipPath')
    clipPath.appendChild(path)

    this.getInnerChartG().prepend(clipPath)
    chartG.setAttribute('clip-path', 'url(#clip)')
  }

  setLevels = () => {
    const parentG = document.querySelector('#radar-chart>div>svg>g>g')!

    const levelsG = (parentG.childNodes[0] as Element).querySelector(
      'g'
    )! as Element

    const levels: Element[] = []

    levelsG.childNodes.forEach(l => levels.push(l.cloneNode(true) as Element))

    parentG.innerHTML = parentG.innerHTML.replace(/clippath/g, 'clipPath')

    levels.forEach((l, i) => {
      l.classList.add('level')
      l.classList.add(`level${i}`)
      this.getInnerChartG().appendChild(l)
    })
  }

  updateClipPath = (d: string) => {
    document.getElementById('clipPath')!.setAttribute('d', d)
  }

  setChart = () => {
    const parentG = document.querySelector('#radar-chart>div>svg>g>g')!
    const chartG = (parentG.childNodes[1] as Element)!
    const d = chartG.querySelector('path')!.getAttribute('d')!

    this.updateClipPath(d)
  }
  getKey = () => this.key++

  Label = ({ id, anchor, angle }): any => {
    const { data, smallDevice } = this.state
    const { role } = this.props
    const cur = data.find(d => d.label === id)
    if (!cur) {
      return ''
    }
    const className = getValueClassName(cur.value)

    const lineHeight = docSize() * (smallDevice ? 2 : 2.5)
    const texts = cur.label.split(' ')
    const isLocked =
      checkLockedAbility(this.props.isPlusUser, cur.label) &&
      notFreemium(this.props.isPlusUser, cur.label)
    const educator = isEducator(role)
    const generateTextButton = () => {
      return educator ? 'N/A' : isLocked ? 'Plus' : 'Start'
    }
    const generateValue = () => {
      return !isLocked && cur.value > -1 ? `${cur.value}%` : 'Ready'
    }
    const isValueLocked = generateValue() === 'Ready'

    const anchorMiddle =
      data.length === 9 &&
      (cur.label === 'Social' || cur.label === 'Self-esteem')
        ? 'anchor-middle'
        : ''
    const secondRow =
      cur.label === 'Language' || cur.label === 'Math' ? 'second-row' : ''
    const thirdRow =
      cur.label === 'Memory' || cur.label === 'Spatial' ? 'third-row' : ''

    return (
      <g
        onClick={cur.onClick}
        id={`label-${cur.label}`}
        className={classnames(
          `radar-label`,
          `anchor-${anchor}`,
          `angle-${angle}`,
          {
            'has-two-rows': texts.length === 2,
            'radar-label_educator': educator
          },
          anchorMiddle,
          secondRow,
          thirdRow
        )}
      >
        {texts.map((text, index) => (
          <text key={index} className="label-text" y={lineHeight * index}>
            {text}
          </text>
        ))}
        {cur.value === -1 ? (
          <>
            <rect
              id={isLocked ? 'pink' : ''}
              className="radar-btn__border"
              y={
                lineHeight * texts.length +
                (smallDevice ? texts.length * 2 + 2 : 0)
              }
              rx="10"
              ry="10"
              fillOpacity="0"
              stroke="#DFDFDF"
              strokeWidth="1"
            />
            <text
              id={isLocked ? 'pink' : ''}
              className={classnames('radar-btn__text', anchorMiddle, {
                'radar-btn__text_educator': educator
              })}
              y={
                lineHeight * texts.length +
                (smallDevice ? texts.length * 2 + 2 : 0)
              }
            >
              {generateTextButton()}
            </text>
          </>
        ) : (
          <>
            {isValueLocked && (
              <rect
                id={'pink'}
                className="radar-btn__border"
                y={
                  lineHeight * texts.length +
                  (smallDevice ? texts.length * 2 + 2 : 0)
                }
                rx="10"
                ry="10"
                fillOpacity="0"
                stroke="#DFDFDF"
                strokeWidth="1"
              />
            )}
            <text
              id={isValueLocked ? 'pink' : ''}
              className={'label-value' + (className ? ` ${className}` : '')}
              y={
                lineHeight * texts.length +
                (smallDevice ? texts.length * 2 + 8 : 0)
              }
            >
              {generateValue()}
            </text>
          </>
        )}
      </g>
    )
  }

  commonProperties = () => {
    let fSize = docSize()
    let sideMargin = 14
    if (window.innerWidth <= 575) {
      fSize -= 1.7
      sideMargin += 1.5
      if (window.innerWidth <= 350) {
        sideMargin += 3
      }
    }

    return {
      width: 53 * fSize,
      height: 53 * fSize,
      margin: {
        top: 0,
        right: sideMargin * fSize,
        bottom: 0,
        left: sideMargin * fSize
      }
    }
  }

  render() {
    const { data, smallDevice } = this.state

    return (
      <div
        id="radar-chart"
        className={'radar' + (smallDevice ? ' small' : '') + ' design-fixes'}
      >
        <Radar
          key={this.getKey()}
          {...this.commonProperties()}
          data={data}
          indexBy="label"
          keys={['value']}
          legends={[]}
          gridShape="linear"
          colors={['#9C00D2']}
          maxValue={this.maxValue}
          gridLabel={this.Label}
          fillOpacity={0}
          dotBorderWidth={0.125 * docSize()}
          dotColor={'#ffffff'}
          dotBorderColor={'#8A8A8A'}
          dotSize={0.625 * docSize()}
        />
      </div>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/Chart/index.tsx